import type { SealedRecord } from './crypto';
import { openRecord } from './crypto';

/**
 * The unlocked vault, in memory, deliberately OUTSIDE `Student` and `ClassData`.
 *
 * ── WHY A SEPARATE STORE AND NOT A FIELD ────────────────────────────────────────────────────────
 *
 * Because the codebase persists whole objects in places that are not obvious:
 *
 *   `handleDeleteClass` snapshots the entire in-memory `ClassData` into `deletionHistory` — which is how
 *   the hydrated `studentPrivate` roster ended up duplicated there in plaintext.
 *   `ClassSettingsModal` saves the whole roster through `updateClass`.
 *   `createClass` re-splits an embedded roster on restore.
 *
 * So "put the decrypted names on the student for the display sites" — which is how the CURRENT
 * `realFirstName`/`realLastName` work, via `MERGE_BACK_TO_STUDENT` — guarantees they reach the cloud
 * eventually, by a route nobody is looking at. The earlier assessment said never merge onto `Student`;
 * the architecture review corrected it to `Student` OR `ClassData`. Both are right, and the way to
 * guarantee it is to have no object to merge: the resolver returns STRINGS from this module.
 *
 * ── LIFETIME ────────────────────────────────────────────────────────────────────────────────────
 *
 * Memory only. Nothing here is serialised, so a reload re-reads the ciphertext and re-decrypts with the
 * cached key. Cleared on lock and on sign-out.
 */

/** The decrypted PII for one student. Every field is optional: a teacher may know only a first name. */
export interface VaultRecordPlain {
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  email?: string;
  studentNumber?: string;
  governmentId?: string;
  parentName?: string;
  parentEmail?: string;
  parentPhone?: string;
  pronouns?: string;
  notes?: string;
  accommodations?: string;
}

interface Session {
  dek: CryptoKey;
  /** studentId → decrypted record. */
  plain: Map<string, VaultRecordPlain>;
  /** studentId → the revision that was read, so a write can be one higher. */
  rev: Map<string, number>;
}

/** Keyed `uid:classId`, so one browser profile cannot serve another teacher's session. */
const sessions = new Map<string, Session>();

const sessionKey = (uid: string, classId: string) => `${uid}:${classId}`;

export const isUnlocked = (uid: string, classId: string): boolean =>
  sessions.has(sessionKey(uid, classId));

/**
 * Decrypt a class's records into memory.
 *
 * A record that fails to open is SKIPPED and logged, not fatal: one corrupted or transplanted entry must
 * not stop a teacher seeing the other twenty-nine names. The AAD check means a transplanted record lands
 * here rather than quietly showing the wrong child's name.
 */
export async function unlockClass(
  uid: string,
  classId: string,
  dek: CryptoKey,
  records: Record<string, SealedRecord>,
): Promise<{ opened: number; failed: string[] }> {
  const plain = new Map<string, VaultRecordPlain>();
  const rev = new Map<string, number>();
  const failed: string[] = [];

  for (const [studentId, rec] of Object.entries(records)) {
    try {
      plain.set(studentId, await openRecord<VaultRecordPlain>(rec, dek, { classId, studentId }));
      rev.set(studentId, rec.rev);
    } catch {
      failed.push(studentId);
    }
  }

  sessions.set(sessionKey(uid, classId), { dek, plain, rev });
  return { opened: plain.size, failed };
}

/** Forget one class's decrypted records and key handle. */
export const lockClass = (uid: string, classId: string): void => {
  sessions.delete(sessionKey(uid, classId));
};

/**
 * Forget everything. Used on SIGN-OUT, from every tab.
 *
 * The comment here used to say "and the idle auto-lock". There is no idle auto-lock — nothing anywhere
 * implements one — and naming a feature that does not exist is how a reviewer, or a future reader,
 * concludes that unlocked sessions time out. They do not: with the key cache in place, "unlocked" is the
 * standing state of every browser the teacher has ever unlocked on, until they lock a class or sign out.
 * That is a deliberate trade for usability and it is stated honestly in the walkthrough; it is not
 * something this function does.
 */
export const lockAll = (): void => { sessions.clear(); };

/**
 * One student's decrypted record, or null when the class is locked or the student is not vaulted.
 *
 * Returns a COPY, so a caller that mutates what it got cannot corrupt the session — and cannot hand a
 * live reference to something that persists objects.
 */
export function resolveRecord(
  uid: string,
  classId: string,
  studentId: string,
): VaultRecordPlain | null {
  const got = sessions.get(sessionKey(uid, classId))?.plain.get(studentId);
  return got ? { ...got } : null;
}

/**
 * The display name for a student: real when unlocked and vaulted, otherwise the pseudonym passed in.
 *
 * Takes the fallback rather than reading the student object, so the ~40 display sites can call this with
 * what they already have and there is no path where this function is tempted to return an object.
 */
export function resolveDisplayName(
  uid: string,
  classId: string,
  studentId: string,
  fallback: { firstName: string; lastName: string },
): { firstName: string; lastName: string; isReal: boolean } {
  const rec = sessions.get(sessionKey(uid, classId))?.plain.get(studentId);
  if (!rec?.firstName && !rec?.lastName) return { ...fallback, isReal: false };
  return {
    firstName: rec.firstName || fallback.firstName,
    lastName: rec.lastName || fallback.lastName,
    isReal: true,
  };
}

/**
 * Forget ONE student's decrypted record, keeping the class unlocked.
 *
 * For a record that was written but could not be verified. `saveManyStudentPii` notes every record it seals,
 * including ones that later fail verification — so the session "knows" values whose plaintext is deliberately
 * still on the class document. The leak guard then sees that plaintext on an untouched student and refuses
 * the write, which aborted the blanking for the WHOLE class and turned "protect the students that did
 * verify" into "protect nobody".
 *
 * Forgetting the unverified record restores the invariant: the guard only knows what is genuinely vaulted.
 */
export function forgetRecord(uid: string, classId: string, studentId: string): void {
  const s = sessions.get(sessionKey(uid, classId));
  s?.plain.delete(studentId);
  s?.rev.delete(studentId);
}

/** The revision last read for a student, so the next write is one higher. */
export const currentRev = (uid: string, classId: string, studentId: string): number =>
  sessions.get(sessionKey(uid, classId))?.rev.get(studentId) ?? 0;

/** Record the revision a successful write stored. */
export function noteRev(uid: string, classId: string, studentId: string, rev: number): void {
  sessions.get(sessionKey(uid, classId))?.rev.set(studentId, rev);
}

/** Update the in-memory record after a successful write, so the UI reflects it without a re-read. */
export function noteRecord(
  uid: string,
  classId: string,
  studentId: string,
  plain: VaultRecordPlain,
): void {
  sessions.get(sessionKey(uid, classId))?.plain.set(studentId, { ...plain });
}

export const unlockedDek = (uid: string, classId: string): CryptoKey | null =>
  sessions.get(sessionKey(uid, classId))?.dek ?? null;

/**
 * Every value currently decrypted for a class, for the leak guard.
 *
 * Exported for `vaultLeakGuard`, which needs to recognise a real name by VALUE. A key allowlist cannot do
 * that job here: the resolver returns a real name in `firstName`, which is a legitimate field on
 * `Student`, so a guard that inspects key names sees nothing wrong. Only comparing values can tell a
 * pseudonym from the name it stands for.
 */
export function knownPlainValues(uid: string, classId: string): Set<string> {
  const out = new Set<string>();

  /**
   * MATCHED BY CLASS, NOT BY UID — because four of the five guard call sites pass
   * `auth.currentUser?.uid ?? ''`.
   *
   * An exact `sessions.get(uid + ':' + classId)` found nothing for an empty uid, so the guard became a
   * silent no-op: it returned an empty value set, found no leak, and allowed the write. `currentUser` is
   * briefly null during a token refresh, which is not a rare moment — it happens roughly hourly, in the
   * background, while a teacher is working — and nothing anywhere would have reported it.
   *
   * The uid could have been threaded through properly instead, but then the next call site can get it
   * wrong again. Matching on the class removes the parameter's power to be wrong: a class has ONE vault,
   * so if any session in this browser holds it open, those values are known-decrypted and must not be
   * persisted no matter who is nominally signed in.
   *
   * Unioning across sessions is strictly SAFER, never looser. Two sessions for one class means
   * co-teachers, and the values are the same children's either way; a different class has a different
   * key and is untouched. `uid` is kept in the signature so every caller and test stays valid, and
   * because a future exact-match use is plausible.
   */
  const suffix = `:${classId}`;
  const matching = [...sessions.entries()]
    .filter(([key]) => key.endsWith(suffix))
    .map(([, session]) => session);
  if (!matching.length) return out;

  const add = (v: unknown) => {
    const str = typeof v === 'string' ? v.trim() : '';
    // Two characters or fewer is not identifying and would fire on initials in ordinary prose.
    if (str.length > 2) out.add(str.toLowerCase());
  };

  /**
   * An ALLOWLIST of identifying fields, not a denylist of exceptions.
   *
   * The first version excluded `pronouns` after it turned out that "she/her" — a four-word vocabulary —
   * made every other student with the same pronouns unsaveable. That was the right fix for the wrong shape:
   * the problem is not pronouns, it is every low-cardinality field. `accommodations: 'Extra time'` and
   * `notes: 'None'` collide exactly the same way, and they reach the guard through `updateStudent` BEFORE
   * `splitStudentPii` routes them to the owner-only roster.
   *
   * So this lists what actually identifies a child. Everything else in the vault is there because it is
   * personal, not because it names anyone — and a value that cannot identify anyone is not worth refusing a
   * teacher's save over.
   */
  const IDENTIFYING: ReadonlySet<string> = new Set([
    'firstName', 'lastName', 'preferredName',
    'email', 'studentNumber', 'governmentId',
    'parentName', 'parentEmail', 'parentPhone',
  ]);

  for (const rec of matching.flatMap(session => [...session.plain.values()])) {
    for (const [k, v] of Object.entries(rec)) {
      if (!IDENTIFYING.has(k)) continue;
      add(v);
    }

    /**
     * COMBINED FORMS, because the app stores assembled names as often as it stores fields.
     *
     * `deletionHistory` records carry `name: \`${firstName} ${lastName}\``; submissions carry
     * `studentName`; report exports build "Last, First". None of those match a single field value, so a
     * guard comparing only field values passed them straight through — found by a test that demanded the
     * `name` field be caught and was right to.
     */
    const first = (rec.firstName || '').trim();
    const last = (rec.lastName || '').trim();
    const preferred = (rec.preferredName || '').trim();
    for (const given of [first, preferred]) {
      if (!given || !last) continue;
      add(`${given} ${last}`);
      add(`${last}, ${given}`);
      add(`${last} ${given}`);
    }
  }
  return out;
}
